import React, { useEffect, useMemo, useState } from "react";

// フロントからは常に /api 配下を相対パスで叩く（本番・開発共通）
const API = "";


/* ===== Types ===== */
type User = {
  id: string;
  name?: string;
  totalPoints?: number;
  purchasePt?: number;
  bonusPt?: number;
};

type LedgerGrant = {
  userId: string;
  delta: number;
  reason?: string;
};

type PointOption = {
  id?: string;              // 任意（無ければ生成）
  label?: string;           // 表示ラベル（省略時は "◯pt（¥◯）"）
  points: number;           // 付与pt
  priceJPY: number;         // 課金価格
  isActive?: boolean;       // 公開/非公開
  sort?: number;            // 並び順
};

/* ===== Component ===== */
export default function Points() {
  // --- ユーザー残高/付与 ---
  const [users, setUsers] = useState<User[]>([]);
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState<string>("");
  const [balance, setBalance] = useState<{ total?: number; purchase?: number; bonus?: number }>({});
  const [amount, setAmount] = useState<number>(0);
  const [note, setNote] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<string>("");

  // --- 課金ポイント設定（＝ユーザー側の《ポイント選択》） ---
  const [options, setOptions] = useState<PointOption[]>([]);
  const [optMsg, setOptMsg] = useState<string>("");
  const [optLoading, setOptLoading] = useState<boolean>(false);

  /* ===== Users ===== */
  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        const url = q
          ? `${API}/api/admin/users?query=${encodeURIComponent(q)}`
          : `${API}/api/admin/users`;
        const res = await fetch(url, { signal: controller.signal });
        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        const list: User[] = (Array.isArray(data?.items) ? data.items : data) as any;
        setUsers(list || []);
      } catch {}
    })();
    return () => controller.abort();
  }, [q]);

  const selectedUser = useMemo(
    () => users.find(u => String(u.id) === String(selectedId)),
    [users, selectedId]
  );

  const fetchBalance = async (uid: string) => {
    if (!uid) return;
    setLoading(true);
    setMsg("");
    try {
      // サーバ側の実エンドポイント: GET /api/admin/balance?userId=xxxx
      const res = await fetch(
        `${API}/api/admin/balance?userId=${encodeURIComponent(uid)}`
      );

      if (!res.ok) {
        throw new Error("残高APIにアクセスできませんでした。");
      }

      const j = await res.json();
      console.log("admin/balance response", uid, j);  // ← 追加
      setBalance({
        // index.js が返している形式:
        // { purchasePoint, bonusPoint, totalPoint }
        total:
          j?.totalPoint ??
          j?.total ??
          j?.balance ??
          undefined,
        purchase:
          j?.purchasePoint ??
          j?.purchasePt ??
          undefined,
        bonus:
          j?.bonusPoint ??
          j?.bonusPt ??
          undefined,
      });
    } catch (e: any) {
      setMsg(e.message || "残高の取得に失敗しました。");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedId) fetchBalance(selectedId);
  }, [selectedId]);

  const grant = async () => {
    if (!selectedId) {
      setMsg("ユーザーを選択してください。");
      return;
    }
    if (!amount || Number.isNaN(amount)) {
      setMsg("ポイント数を入力（または選択）してください。");
      return;
    }

    setLoading(true);
    setMsg("");

    try {
      // 今回は「ボーナスポイント付与」として扱う
      const body = {
        ids: [Number(selectedId)],                       // 対象ユーザーID配列
        purchaseDelta: 0,                               // 購入PTは増やさない
        bonusDelta: Number(amount),                     // ボーナスPTだけ増やす
        memo: note?.trim() || "manual grant",           // 台帳メモ
      };

      const res = await fetch(`${API}/api/admin/users/grant`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        throw new Error(await res.text());
      }

      setMsg("付与が完了しました。");
      setAmount(0);

      // 付与後に残高を取り直し
      await fetchBalance(selectedId);
    } catch (e: any) {
      setMsg(e.message || "付与に失敗しました。");
    } finally {
      setLoading(false);
    }
  };


  const totalLabel = balance.total ?? selectedUser?.totalPoints ?? undefined;
  const purchaseLabel = balance.purchase ?? selectedUser?.purchasePt ?? undefined;
  const bonusLabel = balance.bonus ?? selectedUser?.bonusPt ?? undefined;

  /* ===== 課金ポイント設定（ユーザー側の《ポイント選択》） ===== */

  const normalizeOptions = (arr: any[]): PointOption[] =>
    (arr || []).map((o, i) => ({
      id: o.id ?? `${o.points}-${o.priceJPY}-${i}`,
      label: o.label,
      points: Number(o.points ?? 0),
      priceJPY: Number(o.priceJPY ?? 0),
      isActive: !!o.isActive,
      sort: Number(o.sort ?? i),
    }));

  const fetchOptions = async () => {
    setOptLoading(true);
    setOptMsg("");
    try {
      // 1st: 推奨
      let res = await fetch(`${API}/api/admin/points/options`);
      if (res.ok) {
        const j = await res.json();
        setOptions(normalizeOptions(j?.options || j));
        return;
      }
      // 2nd: 互換（settings形式）
      res = await fetch(`${API}/api/admin/points/settings`);
      if (res.ok) {
        const j = await res.json();
        setOptions(normalizeOptions(j?.purchaseOptions || j?.options || []));
        return;
      }
      // 3rd: 公開APIを流用（最悪でも参照可）
      res = await fetch(`${API}/api/points/options`);
      if (res.ok) {
        const j = await res.json();
        setOptions(normalizeOptions(j?.options || j));
        return;
      }
      throw new Error("課金ポイント設定APIにアクセスできませんでした。");
    } catch (e: any) {
      setOptMsg(e.message || "課金ポイント設定の取得に失敗しました。");
    } finally {
      setOptLoading(false);
    }
  };

  const saveOptions = async () => {
    setOptLoading(true);
    setOptMsg("");
    try {
      const payload = { options: options
        .slice()
        .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
        .map(o => ({ id: o.id, label: o.label, points: o.points, priceJPY: o.priceJPY, isActive: !!o.isActive, sort: o.sort })) };

      // まずは /api/admin/points/options にPUT（またはPOST）を試行
      let res = await fetch(`${API}/api/admin/points/options`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        // POSTで運用している構成にも対応
        res = await fetch(`${API}/api/admin/points/options`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      }
      if (!res.ok) {
        // settings形式
        res = await fetch(`${API}/api/admin/points/settings`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ purchaseOptions: payload.options }),
        });
      }
      if (!res.ok) throw new Error(await res.text());
      setOptMsg("課金ポイント設定を保存しました。");
      await fetchOptions();
    } catch (e: any) {
      setOptMsg(e.message || "保存に失敗しました。");
    } finally {
      setOptLoading(false);
    }
  };

  useEffect(() => {
    fetchOptions();
  }, []);

  const addRow = () => {
    const maxSort = Math.max(-1, ...options.map(o => o.sort ?? 0));
    setOptions(prev => [
      ...prev,
      { id: `new-${Date.now()}`, points: 0, priceJPY: 0, isActive: true, sort: maxSort + 1 },
    ]);
  };

  const removeRow = (idx: number) => {
    setOptions(prev => prev.filter((_, i) => i !== idx));
  };

  const move = (idx: number, dir: -1 | 1) => {
    setOptions(prev => {
      const arr = prev.slice();
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return prev;
      const a = arr[idx];
      const b = arr[j];
      const sa = a.sort ?? idx;
      const sb = b.sort ?? j;
      a.sort = sb;
      b.sort = sa;
      arr[idx] = b;
      arr[j] = a;
      return arr.slice();
    });
  };

  /* ===== Render ===== */

  return (
    <div style={{ padding: 16 }}>
      <h1 style={{ margin: "8px 0 16px", fontSize: 20, fontWeight: 800 }}>ポイント管理</h1>

      {/* === 残高＆付与 === */}
      <div style={{ background: "#fff", borderRadius: 12, padding: 16, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <label style={{ fontWeight: 700 }}>ユーザー検索</label>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="ID / 名前 / メール など"
            style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid #ddd", minWidth: 220 }}
          />
          <select
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid #ddd", minWidth: 220 }}
          >
            <option value="">ユーザーを選択</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name ? `${u.name} (${u.id})` : u.id}
              </option>
            ))}
          </select>

          <button
            onClick={() => selectedId && fetchBalance(selectedId)}
            disabled={!selectedId || loading}
            style={{
              padding: "8px 12px",
              borderRadius: 8,
              border: "1px solid #1976d2",
              background: "#1976d2",
              color: "#fff",
              fontWeight: 700,
            }}
            aria-label="残高を更新"
          >
            残高を更新
          </button>
        </div>

        <div style={{ marginTop: 12, display: "flex", gap: 24, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 12, color: "#667" }}>保有ポイント</div>
            <div style={{ fontSize: 28, fontWeight: 900 }}>
              {totalLabel ?? "—"} <span style={{ fontSize: 16, fontWeight: 700 }}>pt</span>
            </div>
            <div style={{ fontSize: 12, color: "#667" }}>
              内訳：購入 {purchaseLabel ?? "—"} pt / ボーナス {bonusLabel ?? "—"} pt
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          marginTop: 16,
          background: "#fff",
          borderRadius: 12,
          padding: 16,
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        }}
      >
        <div style={{ fontWeight: 800, marginBottom: 12 }}>ポイント付与</div>

        <div style={{ display: "grid", gap: 12 }}>
          <div>
            <div style={{ fontSize: 12, color: "#667", marginBottom: 6 }}>STEP1. 追加するポイントを選択/入力</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {[100, 300, 500, 1000, 3000, 5000, 10000].map((v) => (
                <button
                  key={v}
                  onClick={() => setAmount(v)}
                  style={{
                    padding: "8px 10px",
                    borderRadius: 8,
                    border: "1px solid #ddd",
                    background: amount === v ? "#e8f2ff" : "#fff",
                    fontWeight: 700,
                  }}
                >
                  {v} pt
                </button>
              ))}
              <input
                type="number"
                value={Number.isNaN(amount) ? 0 : amount}
                onChange={(e) => setAmount(Number(e.target.value))}
                placeholder="任意のpt"
                style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid #ddd", width: 140 }}
              />
            </div>
          </div>

          <div>
            <div style={{ fontSize: 12, color: "#667", marginBottom: 6 }}>STEP2. 理由（台帳メモ）</div>
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="例）キャンペーン付与 / 調整 など"
              style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid #ddd", width: "min(600px, 100%)" }}
            />
          </div>

          <div>
            <button
              onClick={grant}
              disabled={loading}
              style={{
                padding: "10px 14px",
                borderRadius: 10,
                border: "1px solid #2e7d32",
                background: "#2e7d32",
                color: "#fff",
                fontWeight: 800,
                minWidth: 160,
              }}
            >
              {loading ? "処理中..." : "付与する"}
            </button>
          </div>
        </div>

        {!!msg && (
          <div style={{ marginTop: 12, color: msg.includes("失敗") || msg.includes("できません") ? "#b00020" : "#1a7f37" }}>
            {msg}
          </div>
        )}
      </div>

      {/* === 課金ポイント設定（ユーザー側の《ポイント選択》に反映） === */}
      <div
        style={{
          marginTop: 16,
          background: "#fff",
          borderRadius: 12,
          padding: 16,
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <div style={{ fontWeight: 800 }}>課金ポイント設定（ユーザー側《ポイント選択》）</div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={fetchOptions}
              disabled={optLoading}
              style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #1976d2", background: "#1976d2", color: "#fff", fontWeight: 700 }}
            >
              取得
            </button>
            <button
              onClick={saveOptions}
              disabled={optLoading}
              style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #2e7d32", background: "#2e7d32", color: "#fff", fontWeight: 800 }}
            >
              保存
            </button>
          </div>
        </div>
        <div style={{ fontSize: 12, color: "#667", marginBottom: 10 }}>
          ここで設定した項目がユーザー画面の「ポイント購入 → ポイント選択」に表示されます。
        </div>

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f7f9fc" }}>
                <th style={th}>並び</th>
                <th style={th}>公開</th>
                <th style={th}>ポイント</th>
                <th style={th}>価格(¥)</th>
                <th style={th}>ラベル(任意)</th>
                <th style={th}>操作</th>
              </tr>
            </thead>
            <tbody>
              {options
                .slice()
                .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
                .map((opt, idx) => (
                  <tr key={opt.id || idx} style={{ borderTop: "1px solid #eee" }}>
                    <td style={td}>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button onClick={() => move(idx, -1)} style={btnGhost} aria-label="上へ">▲</button>
                        <button onClick={() => move(idx, +1)} style={btnGhost} aria-label="下へ">▼</button>
                      </div>
                    </td>
                    <td style={td}>
                      <input
                        type="checkbox"
                        checked={!!opt.isActive}
                        onChange={(e) => {
                          const v = e.target.checked;
                          setOptions(prev => prev.map((o, i) => i === idx ? { ...o, isActive: v } : o));
                        }}
                      />
                    </td>
                    <td style={td}>
                      <input
                        type="number"
                        value={opt.points}
                        onChange={(e) => {
                          const v = Number(e.target.value);
                          setOptions(prev => prev.map((o, i) => i === idx ? { ...o, points: v } : o));
                        }}
                        style={inpNum}
                      />
                    </td>
                    <td style={td}>
                      <input
                        type="number"
                        value={opt.priceJPY}
                        onChange={(e) => {
                          const v = Number(e.target.value);
                          setOptions(prev => prev.map((o, i) => i === idx ? { ...o, priceJPY: v } : o));
                        }}
                        style={inpNum}
                      />
                    </td>
                    <td style={td}>
                      <input
                        value={opt.label ?? ""}
                        onChange={(e) => {
                          const v = e.target.value;
                          setOptions(prev => prev.map((o, i) => i === idx ? { ...o, label: v } : o));
                        }}
                        placeholder='例）"1000pt（¥1000）"'
                        style={inpText}
                      />
                    </td>
                    <td style={td}>
                      <button onClick={() => removeRow(idx)} style={{ ...btnGhost, color: "#b00020", borderColor: "#b00020" }}>
                        削除
                      </button>
                    </td>
                  </tr>
                ))}
              {!options.length && (
                <tr>
                  <td colSpan={6} style={{ ...td, color: "#667" }}>設定がありません。「行を追加」から作成してください。</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
          <button onClick={addRow} disabled={optLoading} style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #555", background: "#fff", fontWeight: 700 }}>
            行を追加
          </button>
          {!!optMsg && (
            <div style={{ alignSelf: "center", color: optMsg.includes("失敗") ? "#b00020" : "#1a7f37" }}>
              {optMsg}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ===== small styles ===== */
const th: React.CSSProperties = { textAlign: "left", padding: "10px 8px", fontSize: 12, color: "#445", borderBottom: "1px solid #e5e7eb", whiteSpace: "nowrap" };
const td: React.CSSProperties = { padding: "10px 8px", fontSize: 14, verticalAlign: "middle", whiteSpace: "nowrap" };
const btnGhost: React.CSSProperties = { padding: "6px 8px", borderRadius: 8, border: "1px solid #999", background: "#fff", fontWeight: 700, cursor: "pointer" };
const inpNum: React.CSSProperties = { padding: "6px 8px", width: 120, borderRadius: 8, border: "1px solid #ddd" };
const inpText: React.CSSProperties = { padding: "6px 8px", width: 260, borderRadius: 8, border: "1px solid #ddd" };
